// Medical chronology — PI Platform outline v2 §3.16.
//
// The narrative that goes into the demand, one paragraph per verified
// encounter, editable and saved.
//
// It replaces text the model regenerated on every demand preview. The
// display has to make one thing obvious, because it is the failure this
// domain exists to prevent: an edited paragraph is never rewritten by a
// regeneration. If that isn't visible, nobody trusts their edits enough
// to make them, and they go back to fixing the letter in Word — where
// the correction is lost the next time a document is generated.
//
// So every edited entry is labelled, "Rebuild" says up front how many
// edits it will keep, and each paragraph shows the record page it came
// from. The citation is from our own extraction, not the model.

import React, { useCallback, useEffect, useState } from "react";
import { C } from "../constants.js";
import { apiFetch } from "../api.js";
import { Spinner } from "../components/Spinner.jsx";

export function ChronologyCard({ matterId, showNotification }) {
  const [chron, setChron] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);   // entry id
  const [draft, setDraft] = useState("");
  const [adding, setAdding] = useState(false);
  const [newText, setNewText] = useState("");
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await apiFetch(`/api/matters/${matterId}/chronology`);
      setChron(d);
    } catch (e) {
      showNotification?.(`Chronology failed to load: ${e.message}`, "error");
      setChron({ exists: false, entries: [] });
    }
  }, [matterId, showNotification]);

  useEffect(() => { load(); }, [load]);

  const entries = chron?.entries || [];
  const editedCount = entries.filter((e) => e.is_edited).length;
  const locked = chron?.status === "locked";

  const rebuild = async () => {
    const keeps = editedCount
      ? `\n\n${editedCount} edited paragraph${editedCount === 1 ? "" : "s"} will be kept as ${editedCount === 1 ? "it is" : "they are"}.`
      : "";
    if (entries.length && !window.confirm(
      `Rebuild the chronology from the current treatment records?${keeps}`)) return;
    setBusy(true);
    try {
      const d = await apiFetch(`/api/matters/${matterId}/chronology/generate`,
        { method: "POST" });
      setChron({ exists: true, ...d });
      const kept = d.preserved_edits || 0;
      showNotification?.(
        kept
          ? `Chronology rebuilt. ${kept} edited paragraph${kept === 1 ? "" : "s"} kept.`
          : "Chronology rebuilt.", "success");
      await load();
    } catch (e) {
      showNotification?.(`Rebuild failed: ${e.message}`, "error");
    }
    setBusy(false);
  };

  const saveEdit = async (id) => {
    if (!draft.trim()) {
      showNotification?.("A paragraph can't be empty.", "warning");
      return;
    }
    try {
      await apiFetch(`/api/chronology-entries/${id}`, {
        method: "PATCH", body: JSON.stringify({ text: draft.trim() }),
      });
      setEditing(null);
      setDraft("");
      await load();
    } catch (e) {
      showNotification?.(`Couldn't save: ${e.message}`, "error");
    }
  };

  const revert = async (entry) => {
    if (!window.confirm(
      "Discard your edit and go back to the generated text?")) return;
    try {
      await apiFetch(`/api/chronology-entries/${entry.id}/revert`,
        { method: "POST" });
      await load();
    } catch (e) {
      showNotification?.(`Revert failed: ${e.message}`, "error");
    }
  };

  const toggleIncluded = async (entry) => {
    try {
      await apiFetch(`/api/chronology-entries/${entry.id}/included`, {
        method: "PATCH",
        body: JSON.stringify({ is_included: !entry.is_included }),
      });
      await load();
    } catch (e) {
      showNotification?.(`Update failed: ${e.message}`, "error");
    }
  };

  const removeEntry = async (entry) => {
    if (!window.confirm("Delete this paragraph?")) return;
    try {
      await apiFetch(`/api/chronology-entries/${entry.id}`, { method: "DELETE" });
      await load();
    } catch (e) {
      showNotification?.(`Delete failed: ${e.message}`, "error");
    }
  };

  const addManual = async () => {
    if (!newText.trim()) return;
    try {
      await apiFetch(`/api/matters/${matterId}/chronology/entries`, {
        method: "POST", body: JSON.stringify({ text: newText.trim() }),
      });
      setNewText("");
      setAdding(false);
      await load();
    } catch (e) {
      showNotification?.(`Couldn't add: ${e.message}`, "error");
    }
  };

  const setLock = async (lock) => {
    try {
      await apiFetch(
        `/api/matters/${matterId}/chronology/${lock ? "lock" : "unlock"}`,
        { method: "POST", body: lock ? JSON.stringify({}) : undefined });
      await load();
    } catch (e) {
      showNotification?.(`${lock ? "Lock" : "Unlock"} failed: ${e.message}`, "error");
    }
  };

  if (chron === null) {
    return React.createElement("div", { style: cardStyle },
      React.createElement("div", {
        style: { display: "flex", alignItems: "center", gap: "8px" },
      },
        React.createElement(Spinner, { size: 16 }),
        React.createElement("span", {
          style: { fontSize: "13px", color: C.textMuted },
        }, "Loading chronology…")));
  }

  return React.createElement("div", { style: cardStyle },
    React.createElement("div", {
      style: { display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" },
    },
      React.createElement("h2", { style: h2Style }, "Medical chronology"),
      locked && React.createElement("span", {
        style: {
          fontSize: "10px", fontWeight: 700, textTransform: "uppercase",
          letterSpacing: "0.06em", color: C.textMuted,
          border: `1px solid ${C.warmGray}`, borderRadius: "4px",
          padding: "2px 7px",
        },
      }, "Locked"),
      React.createElement("div", { style: { flex: 1 } }),
      entries.length > 0 && React.createElement("button", {
        onClick: () => setOpen((v) => !v), style: quietBtn,
      }, open ? "Collapse" : `Show ${entries.length} paragraph${entries.length === 1 ? "" : "s"}`),
    ),

    React.createElement("p", {
      style: { fontSize: "13px", color: C.textMuted, margin: "6px 0 14px" },
    }, entries.length === 0
        ? "Nothing built yet. This is the treatment narrative for the demand — one paragraph per verified encounter, which you can then edit."
        : "Goes into the demand as written here. Paragraphs you edit are kept when the chronology is rebuilt."),

    // The reassurance that makes editing worth doing.
    editedCount > 0 && React.createElement("div", {
      style: {
        fontSize: "12px", color: C.textDark, background: C.warmWhite,
        borderRadius: "8px", padding: "10px 13px", marginBottom: "12px",
      },
    }, editedCount === 1
        ? "1 paragraph has been edited. A rebuild leaves it untouched."
        : `${editedCount} paragraphs have been edited. A rebuild leaves them untouched.`),

    locked && React.createElement("div", {
      style: {
        fontSize: "12px", color: C.textDark, background: C.warmWhite,
        borderRadius: "8px", padding: "10px 13px", marginBottom: "12px",
      },
    }, "Frozen against a sent demand, so what the letter said stays "
     + "recoverable. Unlock to redraft."),

    React.createElement("div", {
      style: { display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: open ? "14px" : 0 },
    },
      !locked && React.createElement("button", {
        onClick: rebuild, disabled: busy, style: primaryBtn,
      }, busy ? "Building…" : entries.length ? "Rebuild from records" : "Build chronology"),
      entries.length > 0 && !locked && React.createElement("button", {
        onClick: () => setAdding((v) => !v), style: quietBtn,
      }, "Add a paragraph"),
      entries.length > 0 && React.createElement("button", {
        onClick: () => setLock(!locked), style: quietBtn,
      }, locked ? "Unlock" : "Lock"),
    ),

    adding && !locked && React.createElement("div", { style: { marginTop: "12px" } },
      React.createElement("textarea", {
        value: newText, onChange: (e) => setNewText(e.target.value),
        rows: 3, placeholder: "A paragraph the records don't cover — a phone call, a conversation.",
        style: textareaStyle,
      }),
      React.createElement("div", { style: { display: "flex", gap: "8px", marginTop: "8px" } },
        React.createElement("button", { onClick: addManual, style: primaryBtn }, "Add"),
        React.createElement("button", {
          onClick: () => { setAdding(false); setNewText(""); }, style: quietBtn,
        }, "Cancel"),
      ),
    ),

    open && entries.map((e) => React.createElement("div", {
      key: e.id,
      style: {
        borderTop: `1px solid ${C.warmGray}`, padding: "12px 0",
        opacity: e.is_included ? 1 : 0.5,
      },
    },
      React.createElement("div", {
        style: { display: "flex", gap: "8px", alignItems: "baseline", flexWrap: "wrap" },
      },
        React.createElement("span", {
          style: { fontSize: "12px", fontWeight: 600, color: C.navy },
        }, e.date_of_service || "No date"),
        e.provider_name && React.createElement("span", {
          style: { fontSize: "12px", color: C.textMuted },
        }, e.provider_name),
        e.is_edited && React.createElement("span", { style: tagStyle },
          e.is_manual ? "Written by hand" : "Edited"),
        React.createElement("div", { style: { flex: 1 } }),
        // Citation comes from the extraction record, never the model.
        (e.citations || []).map((c) => React.createElement("span", {
          key: c.id,
          style: { fontSize: "10px", color: C.textMuted },
          title: c.source_label || "",
        }, c.source_page != null ? `Records p.${c.source_page}` : "Records")),
      ),

      editing === e.id
        ? React.createElement("div", { style: { marginTop: "8px" } },
            React.createElement("textarea", {
              value: draft, onChange: (ev) => setDraft(ev.target.value),
              rows: 4, style: textareaStyle,
            }),
            React.createElement("div", {
              style: { display: "flex", gap: "8px", marginTop: "8px" },
            },
              React.createElement("button", {
                onClick: () => saveEdit(e.id), style: primaryBtn,
              }, "Save"),
              React.createElement("button", {
                onClick: () => { setEditing(null); setDraft(""); }, style: quietBtn,
              }, "Cancel"),
            ),
          )
        : React.createElement("p", {
            style: { fontSize: "13px", color: C.textDark, margin: "6px 0 8px", lineHeight: 1.55 },
          }, e.text || "(empty)"),

      editing !== e.id && !locked && React.createElement("div", {
        style: { display: "flex", gap: "8px", flexWrap: "wrap" },
      },
        React.createElement("button", {
          onClick: () => { setEditing(e.id); setDraft(e.text || ""); }, style: quietBtn,
        }, "Edit"),
        e.is_edited && !e.is_manual && React.createElement("button", {
          onClick: () => revert(e), style: quietBtn,
        }, "Revert to generated"),
        React.createElement("button", {
          onClick: () => toggleIncluded(e), style: quietBtn,
        }, e.is_included ? "Leave out of demand" : "Include in demand"),
        e.is_manual && React.createElement("button", {
          onClick: () => removeEntry(e), style: quietBtn,
        }, "Delete"),
      ),
    )),
  );
}

const cardStyle = {
  background: "#fff", borderRadius: "12px", padding: "20px 24px",
  border: "1px solid #E8E3DC", marginBottom: "20px",
};
const h2Style = {
  fontSize: "13px", color: "#6B6660", textTransform: "uppercase",
  letterSpacing: "0.08em", fontWeight: 600, margin: 0,
};
const quietBtn = {
  border: "1px solid #E8E3DC", background: "#fff", borderRadius: "6px",
  padding: "5px 11px", fontSize: "11px", fontWeight: 600,
  color: "#1B2A4A", cursor: "pointer", flexShrink: 0,
};
const primaryBtn = {
  border: "1px solid #1B2A4A", background: "#1B2A4A", borderRadius: "6px",
  padding: "5px 13px", fontSize: "11px", fontWeight: 600,
  color: "#fff", cursor: "pointer", flexShrink: 0,
};
const textareaStyle = {
  width: "100%", padding: "9px 11px", borderRadius: "6px",
  border: "1px solid #E8E3DC", fontSize: "13px", lineHeight: 1.55,
  fontFamily: "inherit", boxSizing: "border-box", resize: "vertical",
};
const tagStyle = {
  fontSize: "10px", fontWeight: 700, textTransform: "uppercase",
  letterSpacing: "0.06em", color: "#6B6660",
  border: "1px solid #E8E3DC", borderRadius: "4px", padding: "1px 6px",
};
